import React, { Dispatch, useContext, useMemo } from 'react';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';
import { DescriptorJblOptionText } from '@r6digital/rapidstor-types/types/Descriptor';

import { formatCurrency } from '@/utils/currency';
import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/free-regular-svg-icons';
import { faL, fas, faT, IconPrefix } from '@fortawesome/free-solid-svg-icons';
import { IconName } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { formatDealCustomText, stringifyNumber } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { UpgradeDescriptor } from '@/features/rapidstor/UnitOptions';

import unitListingClasses from '../../scss/screens/unit-listing.module.scss';
import classes from '../Option.module.scss';

import Level0 from '../../assets/icon-floor-0.svg?react';
import Level1 from '../../assets/icon-floor-1.svg?react';
import DriveUp from '../DriveUp';

library.add(fas);
library.add(far);
library.add(faL);
library.add(faT)

interface Props extends React.HTMLAttributes<HTMLElement> {
    option: UnitType;
    dealObject?: Deal;
    initialRate?: number;
    desktopOption?: boolean;
    location: Location;
    selected: boolean;
    default?: boolean;
    showTotalPrice?: boolean;
    wizard?: boolean;
    optionText?: DescriptorJblOptionText;
    descriptor?: UpgradeDescriptor;

    setSelectedID: Dispatch<React.SetStateAction<string>>;
}

export default function BaseOption(props: Props) {
    const { setSelectedDescriptor, setSelectedUnitType, reservationData } = useContext(JourneyContext);
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    const formattedAccessLocation = useMemo(() => {
        switch (props.option.units[0]?.iEntryLoc) {
            case 1:
                return 'Drive Up Access';
            default:
                return 'Hallway Access';
        }
    }, [props.option.units]);

    const formattedFloor = useMemo(() => {
        return `${stringifyNumber(props.option.floor)} Floor`;
    }, [props.option.floor]);

    const optionHeader = useMemo(() => {
        if (props.descriptor) {
            return props.descriptor.name;
        } else {
            if (props.optionText?.heading) {
                return props.optionText.heading;
            }
            if (formattedAccessLocation !== 'Drive Up Access') {
                return formattedFloor;
            } else {
                return formattedAccessLocation;
            }
        }
    }, [formattedAccessLocation, formattedFloor, props.descriptor, props.optionText]);

    const optionLabel = useMemo(() => {
        if (props.descriptor) {
            return props.descriptor.upgradeReason ?? props.descriptor.specialText;
        } else {
            if (props.optionText?.subheading) {
                return props.optionText.subheading;
            }
            if (formattedAccessLocation !== 'Drive Up Access') {
                return props.option.floor === 0 ? 'Most Popular' : 'Lift Access';
            } else {
                return 'Easiest Access';
            }
        }
    }, [formattedAccessLocation, props.descriptor, props.option.floor, props.optionText]);

    const makeFontAwesomeIcon = (prefix: string, iconName: string) => {
        if (!iconName) {
            return null;
        }

        const iconStyleMappings: Record<string, string> = {
            'fa-solid': 'fas',
            'fa-regular': 'faR',
            'fa-light': 'faL',
            'fa-thin': 'faT',
        };

        const iconParts = iconName.split('-');
        const formattedIconName = iconParts.slice(1).join('-') as IconName;
        const iconStyle = iconStyleMappings[prefix];
        return <FontAwesomeIcon icon={[iconStyle as IconPrefix, formattedIconName]} />;
    }

    const optionIcon = useMemo(() => {
        if (props.descriptor) {
            return makeFontAwesomeIcon(props.descriptor.upgradeIconPrefix, props.descriptor.upgradeIcon);
        } else {
            return formattedAccessLocation === 'Drive Up Access' ? (
                <DriveUp />
            ) : props.option.floor === 0 ? (
                <Level0 />
            ) : (
                <Level1 />
            )
        }
    }, [formattedAccessLocation, props.descriptor, props.option.floor]);

    /**
     * The idea here is that we only pass the deal explicity to this component if
     * it is a UnitType->UnitType upsell. If that is passed down we return that deal.
     * 
     * If it is a Descriptor->Descriptor upsell such as CRUK or CNCK, we need to
     * find the same deal from the next descriptor OR return the first deal from
     * the descriptor if it is not found.
     */
    const dealObject = useMemo((): Deal | undefined => {
        if (props.dealObject) return props.dealObject;

        if (reservationData?.dealObject) {
            const dealFromOption = props.option.deals.find(
                (deal) => deal._id === reservationData.dealObject?._id
            );

            if (dealFromOption) {
                return dealFromOption;
            }

            if (props.option.deals.length) return props.option.deals[0];
        }

        return undefined;
    }, [props.dealObject, props.option.deals, reservationData]);

    /**
     * Helper memo for displaying the price difference between the selected unitType and the upsell unitType.
     */
    const priceDifferenceDisplay = useMemo(() => {
        const optionRate = dealObject
            ? (dealObject.rates.discountedRate ?? props.option.rates.discountedRate)
            : props.option.rates.rate;

        const priceDifference = optionRate - (props.initialRate ?? 0);

        if (props.default) {
            return formatCurrency(optionRate, props.location);
        }

        if (props.showTotalPrice) {
            return formatCurrency(optionRate, props.location);
        }

        if (priceDifference === 0) {
            return `${formatCurrency(optionRate, props.location, false, false)}`;
        }

        if (priceDifference > 0) {
            return `+${formatCurrency(
                priceDifference ?? 0,
                props.location,
                false,
                false
            )}`;
        } else {
            return `-${formatCurrency(
                priceDifference ?? 0,
                props.location,
                false,
                false
            )}`;
        }
    }, [dealObject, props.default, props.initialRate, props.location, props.option.rates.discountedRate, props.option.rates.rate, props.showTotalPrice]);

    return (
        <div
            className={`${classes.options} ${props.desktopOption === true && classes.desktopOption
                } ${dealObject && classes.deal_option} ${props.wizard && classes.wizard_option
                }`}
            onClick={() => {
                firebaseLogEvent('unit_option_selected', {});
                props.setSelectedID(props.option._id);
                setSelectedUnitType(props.option);
                if (props.descriptor) {
                    setSelectedDescriptor(props.descriptor);
                }
            }}
        >
            {dealObject && props.desktopOption && (
                <div className={`${classes.deal_banner}`}>
                    <span className={`${classes.title}`}>{dealObject?.title}</span>
                </div>
            )}
            <div
                className={`${classes.option_content} ${(dealObject || props.descriptor) && classes.deal_option_content}`}
            >
                <div className={`${classes.icon}`}>
                    {optionIcon}
                </div>
                <div className={`${classes.copy}`}>
                    <span className={`${classes.header}`}>{optionHeader}</span>
                    {!props.descriptor && (<label className={`${classes.label}`}>{optionLabel}</label>)}
                </div>
                <div className={`${classes.price_container}`}>
                    <div className={`${classes.price}`}>
                        <span className={`${classes.display}`}>{priceDifferenceDisplay}</span>
                    </div>
                    <div className={`${classes.radio_wrapper}`}>
                        <input
                            className={`${classes.radio}`}
                            type='checkbox'
                            checked={props.selected}
                            onChange={() => {
                                props.setSelectedID(props.option._id);
                                setSelectedUnitType(props.option);
                                if (props.descriptor) {
                                    setSelectedDescriptor(props.descriptor);
                                }
                            }}
                            />
                    </div>
                    {dealObject && (
                        <div className={`${unitListingClasses.deal_copy} ${classes.deal_copy}`}>
                            <span className={unitListingClasses.text}>
                                {formatDealCustomText(
                                    dealObject,
                                    props.location,
                                    dealObject?.customFormat.primary
                                )}
                            </span>
                            <span className={unitListingClasses.text}>
                                {formatDealCustomText(
                                    dealObject,
                                    props.location,
                                    dealObject?.customFormat.secondary
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {props.descriptor && (<label className={`${classes.descriptor_upsell_label}`}>{optionLabel}</label>)}            
        </div>
    );
}
